import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CalendarDays, TrendingUp, Users } from 'lucide-react'

interface StatCardProps {
    icon: React.ReactNode
    label: string
    value: number | string
    iconBg: string
    iconColor: string
}

const StatCard: React.FC<StatCardProps> = ({
    icon,
    label,
    value,
    iconBg,
    iconColor,
}) => (
    <div className="bg-white rounded-sm p-4 border border-gray-100">
        <div className="flex items-center gap-3">
            <div
                className={`w-10 h-10 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
            >
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-xs text-muted-foreground truncate">
                    {label}
                </p>
                <p className="text-xl font-semibold text-foreground">{value}</p>
            </div>
        </div>
    </div>
)

interface StatsCardsProps {
    totalEmployees: number
    totalWorkDays: number | string
    totalLateDays: number
    avgAttendance: number | string
}

/**
 * Summary statistics cards for WorkDay Dashboard
 */
export const StatsCards: React.FC<StatsCardsProps> = ({
    totalEmployees,
    totalWorkDays,
    totalLateDays,
    avgAttendance,
}) => {
    const { t } = useTranslation()

    return (
        <div
            className="grid grid-cols-2 md:grid-cols-4 gap-2"
            data-tour="workday-stats"
        >
            <StatCard
                icon={<Users className="w-5 h-5" />}
                label={t('workday.total_employees')}
                value={totalEmployees}
                iconBg="bg-blue-50"
                iconColor="text-blue-600"
            />
            <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label={t('workday.total_work_days')}
                value={totalWorkDays}
                iconBg="bg-green-50"
                iconColor="text-green-600"
            />
            <StatCard
                icon={<AlertTriangle className="w-5 h-5" />}
                label={t('workday.total_late_days')}
                value={totalLateDays}
                iconBg="bg-red-50"
                iconColor="text-red-600"
            />
            <StatCard
                icon={<CalendarDays className="w-5 h-5" />}
                label={t('workday.avg_attendance')}
                value={avgAttendance}
                iconBg="bg-purple-50"
                iconColor="text-purple-600"
            />
        </div>
    )
}
